// The two steps an SOS goes through on the admin side (0277), each behind its
// own dialog so neither can happen by accident:
//
//   Acknowledge — a confirm. Taking an SOS is public ("Handled by …") and tells
//                 her someone has it, so it is never a stray click.
//   Resolve     — a required note. The only step that takes an SOS off the live
//                 screens, so it costs a sentence of what happened.

import { useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@bednbody/ui/components/alert-dialog";
import { Button } from "@bednbody/ui/components/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@bednbody/ui/components/dialog";
import { Field, FieldDescription, FieldLabel } from "@bednbody/ui/components/field";
import { Textarea } from "@bednbody/ui/components/textarea";
import { toast } from "sonner";
import { useSosActions } from "./sos.hooks";
import type { SosAlertView } from "./sos.model";

const NOTE_MAX = 2000;

function errorText(error: unknown): string | undefined {
  return error instanceof Error ? error.message : undefined;
}

interface SosDialogProps {
  alert: SosAlertView;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function AcknowledgeSosDialog({ alert, open, onOpenChange }: SosDialogProps) {
  const actions = useSosActions();
  const busy = actions.pendingId === alert.id;

  async function confirm() {
    try {
      await actions.acknowledge(alert.id);
      onOpenChange(false);
    } catch (error) {
      toast.error("Couldn't take the SOS", { description: errorText(error) });
    }
  }

  return (
    <AlertDialog onOpenChange={onOpenChange} open={open}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            Take {alert.therapistName}'s SOS? <span className="font-mono">{alert.ref}</span>
          </AlertDialogTitle>
          <AlertDialogDescription>
            Everyone will see that you're handling it, and {alert.therapistName} is told her hub has
            it. It stays on screen until you mark it resolved.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
          <AlertDialogAction loading={busy} onClick={() => void confirm()} variant="sos">
            I'll handle it
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export function ResolveSosDialog({ alert, open, onOpenChange }: SosDialogProps) {
  const actions = useSosActions();
  const busy = actions.pendingId === alert.id;
  const [note, setNote] = useState("");
  const trimmed = note.trim();
  const noteId = `sos-resolve-note-${alert.id}`;

  function change(next: boolean) {
    if (!next) setNote("");
    onOpenChange(next);
  }

  async function submit() {
    if (!trimmed) return;
    try {
      await actions.resolve(alert.id, trimmed);
      change(false);
    } catch (error) {
      toast.error("Couldn't resolve the SOS", { description: errorText(error) });
    }
  }

  return (
    <Dialog onOpenChange={change} open={open}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            Resolve {alert.therapistName}'s SOS <span className="font-mono">{alert.ref}</span>
          </DialogTitle>
          <DialogDescription>
            It leaves the live screens and moves to SOS history. {alert.therapistName} is told it's
            closed — she never sees your note.
          </DialogDescription>
        </DialogHeader>

        <Field>
          <FieldLabel htmlFor={noteId}>What happened?</FieldLabel>
          <Textarea
            id={noteId}
            maxLength={NOTE_MAX}
            onChange={(event) => setNote(event.target.value)}
            placeholder="e.g. Called her — she's safe and back at the hub."
            required
            rows={4}
            value={note}
          />
          <FieldDescription>
            Required. Kept with your name in SOS history. Don't include a client's contact details —
            hub managers read this too.
          </FieldDescription>
        </Field>

        <DialogFooter>
          <Button disabled={busy} onClick={() => change(false)} variant="outline">
            Cancel
          </Button>
          <Button disabled={!trimmed} loading={busy} onClick={() => void submit()}>
            Mark resolved
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
